import * as React from "react";
import { useToggle } from "react-use";
import { Download, Edit, FolderOutput, Languages, Share, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import * as analytics from "@/services/analytics";

import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { Item } from "@/types/fse";
import { RenameModal } from "../modals/renameModal";
import { ShareModal } from "../modals/shareModal";
import { DeleteModal } from "@/views/drive/modals/deleteModal";
import { UnwrapFolderModal } from "@/views/drive/modals/unwrapFolderModal";
import { formatFolderContentsCount } from "@/views/drive/components/driverRowName/folderCounts";
import { SHAREPOINT_FOLDER_NAME } from "@/views/drive/consts";
import { useTranslateFileMutation } from "@/api/files";
import { TranslateDialog } from "@/views/drive/components/translateDialog";
import { originalDocumentDownloadUrl } from "./downloadOriginalDocument";

interface Props {
  item: Item;
  variant: "empty" | "ghost";
  children: React.ReactNode;
  onRemove?: () => void;
  onUnwrap?: () => void;
  isFolder?: boolean;
  isRootFolder?: boolean;
  currentFolderId: string | null;
  triggerAriaLabel?: string;
  triggerTooltipText?: string;
  additionalItems?: React.ReactNode;
}

export const DriveRowMenu: React.FC<Props> = ({
  isRootFolder,
  variant,
  item,
  children,
  onRemove,
  onUnwrap,
  isFolder,
  currentFolderId,
  triggerAriaLabel,
  triggerTooltipText,
  additionalItems,
}) => {
  const [isRenameOpen, setIsRenameOpen] = React.useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = React.useState(false);
  const [isUnwrapOpen, toggleIsUnwrapOpen] = useToggle(false);
  const [isShareOpen, setIsShareOpen] = React.useState(false);
  const [itemToTranslate, setItemToTranslate] = React.useState<Item | null>(null);
  const user = useAuthenticatedUser();

  const isReadOnly = !item.permissions.some(
    permission => permission.role === "owner" && permission.user_id === user.id,
  );
  const isRejected = item.type === "file" && !!item.rejection_reason;
  const isEmptyFolder = item.type === "folder" && formatFolderContentsCount(item) === "Empty";

  const handleExport = () => {
    analytics.folderExport(item.id, item.name);

    document.location.href = `/pf-mainweb/document/export/${item.id}`;
  };

  const handleTranslate = () => {
    setItemToTranslate(item);
    analytics.translateDocumentOpen();
  };

  const [translateFileMutation, { error: translateFileMutationError }] = useTranslateFileMutation();

  const menuTrigger = (
    <DropdownMenuTrigger asChild>
      <Button aria-label={triggerAriaLabel} variant={variant} className="h-8 w-8 p-0">
        {children}
      </Button>
    </DropdownMenuTrigger>
  );

  return (
    <>
      <RenameModal setIsOpen={setIsRenameOpen} isOpen={isRenameOpen} item={item} />
      <DeleteModal setIsOpen={setIsRemoveOpen} isOpen={isRemoveOpen} item={item} onRemove={onRemove} />
      {item.type === "folder" && (
        <UnwrapFolderModal setIsOpen={toggleIsUnwrapOpen} isOpen={isUnwrapOpen} item={item} onUnwrap={onUnwrap} />
      )}
      {isShareOpen && <ShareModal setIsOpen={setIsShareOpen} isOpen={isShareOpen} item={item} />}

      <DropdownMenu modal={false}>
        {triggerTooltipText ? <Tooltip text={triggerTooltipText}>{menuTrigger}</Tooltip> : menuTrigger}
        <DropdownMenuContent align="end">
          {!isRootFolder && (
            <>
              <DropdownMenuItem disabled={isReadOnly} onSelect={() => setIsRemoveOpen(true)} className="cursor-pointer">
                <Trash2 className="mr-2 h-4 w-4" />
                Remove
              </DropdownMenuItem>
              <DropdownMenuItem disabled={isReadOnly} onSelect={() => setIsRenameOpen(true)} className="cursor-pointer">
                <Edit className="mr-2 h-4 w-4" />
                Rename
              </DropdownMenuItem>
              {item.type === "folder" && item.name !== SHAREPOINT_FOLDER_NAME && (
                <DropdownMenuItem
                  disabled={isReadOnly || isEmptyFolder}
                  onSelect={toggleIsUnwrapOpen}
                  className="cursor-pointer"
                >
                  <FolderOutput className="mr-2 h-4 w-4" />
                  Unwrap folder
                </DropdownMenuItem>
              )}
              {item.name !== SHAREPOINT_FOLDER_NAME && (
                <DropdownMenuItem
                  disabled={isReadOnly}
                  onSelect={() => setIsShareOpen(true)}
                  className="cursor-pointer"
                >
                  <Share className="mr-2 h-4 w-4" />
                  Share
                </DropdownMenuItem>
              )}

              {item.name !== SHAREPOINT_FOLDER_NAME && item.type !== "folder" && (
                <DropdownMenuItem
                  onSelect={handleTranslate}
                  disabled={isRejected || ("original_document_id" in item && !!item.original_document_id)}
                  className="cursor-pointer"
                >
                  <Languages className="mr-2 h-4 w-4" />
                  Translate
                </DropdownMenuItem>
              )}
            </>
          )}
          {additionalItems}
          {item.type === "file" && (
            <DropdownMenuItem asChild className="cursor-pointer">
              <a href={originalDocumentDownloadUrl(item.id)} download={item.name}>
                <Download className="mr-2 h-4 w-4" />
                Download original
              </a>
            </DropdownMenuItem>
          )}
          {isFolder && (
            <DropdownMenuItem className="cursor-pointer" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {itemToTranslate && currentFolderId && (
        <TranslateDialog
          item={itemToTranslate}
          setIsOpen={() => setItemToTranslate(null)}
          isOpen={!!itemToTranslate}
          err={translateFileMutationError}
          id={itemToTranslate.id}
          onConfirm={async data => {
            await translateFileMutation({
              fseId: itemToTranslate.id,
              isScan: data.isScan,
              name: data.name,
              parentFseId: currentFolderId,
              sourceLanguage: data.sourceLang,
              targetLanguage: data.targetLang,
            });
          }}
        />
      )}
    </>
  );
};
